import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TableContainer from "@mui/material/TableContainer";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import { addEditBrands, addEditSeries } from "apis/MyDukanBrandApi";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TableHeader from "components/Table/TableHeader";
import TableElement from "components/Table/TableElement";

export default function SeriesModal({ open, onClose, brand, onSaveSeries }) {
  const [editSeries, setEditSeries] = useState(null);
  const [seriesRows, setSeriesRows] = useState(brand?.series || []);

  const handleEditSeries = (series) => {
    setEditSeries(series);
  };

  const handleAddSeries = () => {
    setEditSeries({ name: "", desc: "", active: true, position: 1 });
  };

  const handleSaveEdit = async (updatedSeries) => {
    if (updatedSeries.seriesId) {
      const payload = {
        seriesId: updatedSeries.seriesId,
        seriesName: updatedSeries.name,
        seriesDesc: updatedSeries.desc,
        position: Number(updatedSeries.position),
        active: updatedSeries.active,
        brandId: brand.id
      };
      await addEditSeries(payload);
    } else {
      const brandPayload = {
        id: brand.id,
        name: brand.name,
        desc: brand.desc,
        category: brand.category,
        categoryId: brand.category,
        position: brand.position,
        active: brand.active,
        displayOnSchemesBook: brand.displayOnSchemesBook || false,
        series: [...seriesRows, { ...updatedSeries }]
      };
      await addEditBrands(brandPayload);
    }
    setEditSeries(null);
  };

  const handleCloseEdit = () => {
    setEditSeries(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 28, fontWeight: 600 }}>
        <span>Series details</span>
        <IconButton onClick={onClose} size="large">
          <CloseIcon sx={{ color: 'red', fontSize: 28 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TableContainer>
          <div className="block w-full overflow-x-auto py-3 px-4 lg:p-8">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr className="divide-x divide-gray-200 ">
                  <TableHeader heading="Name" />
                  <TableHeader heading="Position" />
                  <TableHeader heading="Description" />
                  <TableHeader heading="Active" />
                  <TableHeader heading="Action" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {seriesRows.map((series, idx) => (
                  <tr key={series.seriesId || idx} className="divide-x divide-gray-200 hover:bg-slate-100">
                    <TableElement value={series.name || series.seriesName || "-"} />
                    <TableElement value={series.position} />
                    <TableElement value={series.desc && series.desc.length > 15 ? series.desc.slice(0, 15) + "..." : series.desc} />
                    <TableElement value={series.active ? "True" : "False"} />
                    <TableElement value={
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleEditSeries(series)}
                      >
                        Edit
                      </Button>
                    } />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TableContainer>
        <Button
          variant="contained"
          sx={{ mt: 3, fontWeight: 600, fontSize: 18, px: 4 }}
          onClick={handleAddSeries}
        >
          Add New Series
        </Button>
        {editSeries && (
          <Dialog open={true} onClose={handleCloseEdit} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 22, fontWeight: 600 }}>
              Edit Series
              <IconButton onClick={handleCloseEdit} size="large">
                <CloseIcon sx={{ color: 'red', fontSize: 24 }} />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <TextField
                label="Series Name"
                name="name"
                value={editSeries.name}
                onChange={e => setEditSeries({ ...editSeries, name: e.target.value })}
                fullWidth
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Description"
                name="desc"
                value={editSeries.desc}
                onChange={e => setEditSeries({ ...editSeries, desc: e.target.value })}
                fullWidth
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Position"
                name="position"
                type="number"
                value={editSeries.position}
                onChange={e => setEditSeries({ ...editSeries, position: Number(e.target.value) })}
                fullWidth
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel shrink>Active</InputLabel>
                <Select
                  name="active"
                  value={editSeries.active ? "true" : "false"}
                  onChange={e => setEditSeries({ ...editSeries, active: e.target.value === "true" })}
                  label="Active"
                  displayEmpty
                >
                  <MenuItem value={"true"}>True</MenuItem>
                  <MenuItem value={"false"}>False</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseEdit}>Cancel</Button>
              <Button onClick={() => handleSaveEdit(editSeries)} variant="contained">
                Save
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </DialogContent>
      <DialogActions />
    </Dialog>
  );
}
